<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb mb-1">
        <li class="breadcrumb-item"><a routerLink="/flight-records">Flight Records</a></li>
        <li class="breadcrumb-item active">View Record</li>
      </ol>
    </nav>
    <div class="d-flex align-items-center gap-3">
      <h1 class="h2 mb-0 fw-bold">{{ record?.fieldName || 'Spray Record' }}</h1>
      <span *ngIf="record" class="badge" [ngClass]="getStatusBadgeClass(record.status)">
        {{ getStatusText(record.status) }}
      </span>
    </div>
    <p class="text-muted mb-0 mt-1" *ngIf="record">
      {{ formatDate(record.applicationDate) }}
    </p>
  </div>
  <div class="d-flex gap-2">
    <button class="btn btn-outline-secondary" (click)="goBack()">
      <i class="bi bi-arrow-left me-2"></i>{{ fromOrgId ? 'Back to Organization' : 'Back to List' }}
    </button>
  </div>
</div>

<!-- Loading State -->
<div *ngIf="loading" class="text-center py-5">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
  <p class="text-muted mt-2">Loading flight record...</p>
</div>

<!-- Error State -->
<div *ngIf="!loading && errorMessage" class="alert alert-danger">
  <i class="bi bi-exclamation-triangle me-2"></i>{{ errorMessage }}
</div>

<!-- Main Content -->
<div *ngIf="!loading && record" class="row g-4">
  <!-- Left Column - Main Content -->
  <div class="col-lg-8">

    <!-- Organization -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-building me-2"></i>Organization</h5>
      </div>
      <div class="card-body">
        <a [routerLink]="['/organizations', record.organizationId]" class="text-decoration-none fw-medium">
          {{ record.organizationName || 'Unknown Organization' }}
        </a>
      </div>
    </div>

    <!-- Section 1: Application Details -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-calendar-event me-2"></i>Application Details</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Date</label>
            <p class="mb-0 fw-medium">{{ formatDate(record.applicationDate) }}</p>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Status</label>
            <p class="mb-0">
              <span class="badge" [ngClass]="getStatusBadgeClass(record.status)">
                {{ getStatusText(record.status) }}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 2: Field Information -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-geo-alt me-2"></i>Field Information</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Customer</label>
            <p class="mb-0">{{ customer?.name || '---' }}</p>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Field Name</label>
            <p class="mb-0 fw-medium">{{ field?.name || '---' }}</p>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Acreage</label>
            <p class="mb-0">{{ field?.acreage || 'N/A' }} ac</p>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Crop Type</label>
            <p class="mb-0">{{ field?.crop_type || 'N/A' }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 3: Spray Details -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-droplet me-2"></i>Spray Details</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">What Was Treated</label>
            <p class="mb-0 fw-medium">{{ record.sprayDetails?.whatWasTreated || 'N/A' }}</p>
          </div>
          <div class="col-md-8">
            <label class="form-label text-muted small mb-1">Target Pest</label>
            <p class="mb-0 fw-medium">{{ record.sprayDetails?.targetPest || 'N/A' }}</p>
          </div>
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">Nozzle Description</label>
            <p class="mb-0">{{ record.sprayDetails?.nozzleDescription || 'N/A' }}</p>
          </div>
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">Nozzle to Target Distance</label>
            <p class="mb-0">{{ record.sprayDetails?.nozzleToTargetDistance || 'N/A' }}</p>
          </div>
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">Pump Pressure</label>
            <p class="mb-0">{{ record.sprayDetails?.pumpPressure || 'N/A' }}</p>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Travel Speed</label>
            <p class="mb-0">{{ record.sprayDetails?.travelSpeed || 'N/A' }}</p>
          </div>

          <!-- Drift Management -->
          <div class="col-12" *ngIf="record.sprayDetails?.descriptionOfDriftManagementPractices">
            <label class="form-label text-muted small mb-1">Drift Management Practices</label>
            <p class="mb-0">{{ record.sprayDetails?.descriptionOfDriftManagementPractices }}</p>
          </div>

          <!-- Application Types -->
          <div class="col-12" *ngIf="getApplicationTypeLabels().length > 0">
            <hr class="my-2">
            <label class="form-label text-muted small mb-1">Application Types</label>
            <div class="d-flex flex-wrap gap-2">
              <span *ngFor="let type of getApplicationTypeLabels()" class="badge rounded-pill bg-secondary-subtle text-secondary">
                {{ type }}
              </span>
            </div>
            <p class="mb-0 mt-2" *ngIf="record.applicationTypeInfo?.applicationTypeOther">
              <span class="text-muted">Other:</span> {{ record.applicationTypeInfo?.applicationTypeOther }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 4: Chemical Information -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-flask me-2"></i>Chemical Information</h5>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-striped table-hover mb-0">
            <thead>
              <tr>
                <th class="ps-3">Chemical Name</th>
                <th>EPA Number</th>
                <th>Rate</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngIf="!record.chemicals || record.chemicals.length === 0">
                <td colspan="4" class="text-center text-muted py-3">No chemicals recorded</td>
              </tr>
              <tr *ngFor="let chemical of record.chemicals">
                <td class="ps-3">
                  <span class="fw-medium">{{ chemical.name }}</span>
                  <div class="small text-muted" *ngIf="chemical.templateName">
                    From: {{ chemical.templateName }}
                  </div>
                </td>
                <td>{{ chemical.epaNumber || 'N/A' }}</td>
                <td>{{ chemical.rate || 'N/A' }}</td>
                <td>
                  <span class="badge" [ngClass]="getSourceBadgeClass(chemical.source)">
                    {{ getSourceLabel(chemical.source) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Total Volume and Tank Mix Notes -->
        <div class="p-3 border-top" *ngIf="record.totalVolume || record.tankMixNotes">
          <div class="row g-3">
            <div class="col-md-4" *ngIf="record.totalVolume">
              <label class="form-label text-muted small mb-1">Total Volume</label>
              <p class="mb-0 fw-medium">{{ record.totalVolume }}</p>
            </div>
            <div class="col-md-8" *ngIf="record.tankMixNotes">
              <label class="form-label text-muted small mb-1">Tank Mix Notes</label>
              <p class="mb-0">{{ record.tankMixNotes }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 5: Certification -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-award me-2"></i>Certification</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Certification Name</label>
            <p class="mb-0 fw-medium">{{ certificationName }}</p>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Certificate Number</label>
            <p class="mb-0">{{ certificationNumber }}</p>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">State</label>
            <p class="mb-0">{{ certificationState }}</p>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Expiration Date</label>
            <p class="mb-0"
               [class.text-danger]="isCertificationExpired()"
               [class.text-warning]="isCertificationExpiringSoon()">
              {{ certificationExpiration }}
              <i *ngIf="isCertificationExpiringSoon()" class="bi bi-exclamation-triangle ms-1" title="Expiring soon"></i>
              <i *ngIf="isCertificationExpired()" class="bi bi-x-circle ms-1" title="Expired"></i>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 6: Visual Observer & NOTAM -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-eye me-2"></i>Visual Observer & NOTAM</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">Visual Observer</label>
            <p class="mb-0">{{ record.visualObserverInfo?.visualObserver || 'N/A' }}</p>
          </div>
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">VO Tested</label>
            <p class="mb-0">
              <ng-container *ngIf="record.visualObserverInfo?.voTested">
                <i class="bi bi-check-circle-fill text-success me-1"></i>Yes
              </ng-container>
              <ng-container *ngIf="!record.visualObserverInfo?.voTested">
                <span class="text-muted">Not tested</span>
              </ng-container>
            </p>
          </div>
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">NOTAM Number</label>
            <p class="mb-0">{{ record.visualObserverInfo?.notam || 'N/A' }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 7: Drone Flights -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="adn-icon adn-icon-drone me-2"></i>Drone Flights</h5>
      </div>
      <div class="card-body">
        <div *ngIf="!record.droneFlights || record.droneFlights.length === 0" class="text-center py-3 text-muted">
          No drone flights recorded
        </div>
        <div *ngFor="let flight of record.droneFlights; let i = index; let last = last" class="drone-flight-item">
          <div class="d-flex justify-content-between align-items-start mb-3">
            <div class="d-flex align-items-center gap-2">
              <span class="badge bg-primary rounded-pill">{{ i + 1 }}</span>
                <h6 class="mb-0">{{ flight.droneName || 'Unknown Drone' }}</h6>
                <small class="text-muted">N-Number: {{ flight.droneNNumber || 'N/A' }}</small>
            </div>
            @if(flight.flightPath) {
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="viewFlightPath(flight)">
                <i class="bi bi-map me-1"></i>View Map
              </button>
            }
            
          </div>

          <div class="row g-3">
            <div class="col-md-4">
              <label class="form-label text-muted small mb-1">Pilot</label>
              <p class="mb-0">{{ flight.pilotName || 'Unknown' }}</p>
            </div>
            <div class="col-md-4">
              <label class="form-label text-muted small mb-1">Start Time</label>
              <p class="mb-0">{{ formatTime(flight.startTime) }}</p>
            </div>
            <div class="col-md-4">
              <label class="form-label text-muted small mb-1">End Time</label>
              <p class="mb-0">{{ formatTime(flight.endTime) }}</p>
            </div>
            <div class="col-md-4">
              <label class="form-label text-muted small mb-1">Altitude</label>
              <p class="mb-0">{{ flight.flightAltitude || 'N/A' }} ft</p>
            </div>
            <div class="col-md-4">
              <label class="form-label text-muted small mb-1">Speed</label>
              <p class="mb-0">{{ flight.flightSpeed || 'N/A' }} mph</p>
            </div>
            <div class="col-md-4">
              <label class="form-label text-muted small mb-1">Acreage Sprayed</label>
              <p class="mb-0 fw-medium">{{ flight.acreageSprayed }} ac</p>
            </div>
            <!-- FAA §F.2.d, read-only. Branch on `!= null`, never
                 `@if (flight.numFlights)` — a legacy or hand-rolled 0 is falsy
                 but is a real value and must render as 0, not as "not entered". -->
            <div class="col-md-4">
              <label class="form-label text-muted small mb-1">Number of Flights</label>
              <p class="mb-0">
                @if (flight.numFlights != null) {
                  <span class="fw-medium">{{ flight.numFlights }}</span>
                } @else {
                  <span class="text-muted fst-italic">Not entered &mdash; filed as 1</span>
                }
              </p>
            </div>
            @if(flight.flightPath) {
              <label class="form-label text-muted small mb-1">Flight Path</label>
              <p class="mb-0 mt-0">
                <i class="bi bi-check-circle-fill text-success"></i>
                {{ flight.flightPath.name }}
              </p>
            }
            

            <!-- Chemical Usage for this Drone -->
            <div class="col-12" *ngIf="flight.chemicalUsage && flight.chemicalUsage.length > 0">
              <label class="form-label text-muted small mb-1">Chemical Usage</label>
              <div class="list-group list-group-flush bg-body-tertiary border rounded px-2 py-0">
                <div *ngFor="let cu of flight.chemicalUsage" class="list-group-item bg-transparent d-flex justify-content-between px-0 py-1">
                  <span class="small">{{ cu.chemicalName }}</span>
                  <span class="small fw-medium">{{ cu.amountUsed }}</span>
                </div>
              </div>
            </div>
          </div>

          <hr *ngIf="!last" class="my-4">
        </div>
      </div>
    </div>

    <!-- Section 8: Weather Conditions -->
    <!-- Card stays visible even when `record.weather` is missing so the
         section is consistent with Drone Flights + Incidents (both render
         the card + show an explicit empty state). Body is guarded on
         `record.weather as weather` so legacy/partial API responses don't
         crash inner field accesses; an `else` ng-template surfaces a
         "no data" message instead of silently hiding the entire section. -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-cloud-sun me-2"></i>Weather Conditions</h5>
      </div>
      <div class="card-body">
        <ng-container *ngIf="record.weather as weather; else noWeather">
          <div class="row g-3">
            <div class="col-md-3">
              <label class="form-label text-muted small mb-1">
                <i class="bi bi-thermometer me-1"></i>Temperature
              </label>
              <p class="mb-0 fw-medium">{{ weather.temperature != null ? weather.temperature + '\u00B0F' : 'N/A' }}</p>
            </div>
            <div class="col-md-3">
              <label class="form-label text-muted small mb-1">
                <i class="bi bi-droplet me-1"></i>Humidity
              </label>
              <p class="mb-0">{{ weather.humidity != null ? weather.humidity + '%' : 'N/A' }}</p>
            </div>
            <div class="col-md-3">
              <label class="form-label text-muted small mb-1">
                <i class="bi bi-wind me-1"></i>Wind Speed
              </label>
              <p class="mb-0">{{ weather.windSpeed != null ? weather.windSpeed + ' mph' : 'N/A' }}</p>
            </div>
            <div class="col-md-3">
              <label class="form-label text-muted small mb-1">Wind Direction</label>
              <p class="mb-0">{{ weather.windDirection || 'N/A' }}</p>
            </div>
            <div class="col-12" *ngIf="weather.conditions">
              <label class="form-label text-muted small mb-1">Conditions</label>
              <p class="mb-0">{{ weather.conditions }}</p>
            </div>
          </div>
        </ng-container>
        <ng-template #noWeather>
          <div class="text-center py-3">
            <i class="bi bi-cloud-slash text-muted" style="font-size: 2rem;"></i>
            <p class="mb-0 mt-2 text-muted">Weather data not available</p>
          </div>
        </ng-template>
      </div>
    </div>

    <!-- Section 9: Incident Information -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-exclamation-triangle me-2"></i>Incident Information</h5>
      </div>
      <div class="card-body">
        <ng-container *ngIf="!hasAnyIncidents">
          <div class="text-center py-3">
            <i class="bi bi-check-circle-fill text-success" style="font-size: 2rem;"></i>
            <p class="mb-0 mt-2 text-muted">No incidents reported</p>
          </div>
        </ng-container>

        <ng-container *ngIf="hasAnyIncidents">
          <!-- Takeoff/Landing Damage -->
          <div *ngIf="record.incidentInfo?.takeOffOrLandingDamage" class="mb-3">
            <div class="d-flex align-items-center gap-2 mb-2">
              <i class="bi bi-exclamation-circle text-warning"></i>
              <span class="fw-medium">Take Off or Landing Damage</span>
            </div>
            <p class="mb-0 ps-4" *ngIf="record.incidentInfo?.descriptionOfTakeOffOrLandingDamage">
              {{ record.incidentInfo?.descriptionOfTakeOffOrLandingDamage }}
            </p>
          </div>

          <!-- Equipment Malfunction -->
          <div *ngIf="record.incidentInfo?.equipmentMalfunction" class="mb-3">
            <div class="d-flex align-items-center gap-2 mb-2">
              <i class="bi bi-gear text-warning"></i>
              <span class="fw-medium">Equipment Malfunction</span>
              <span *ngIf="record.incidentInfo?.equipmentMalfunctionType" class="badge bg-warning-subtle text-warning">
                {{ record.incidentInfo?.equipmentMalfunctionType }}
              </span>
            </div>
            <p class="mb-0 ps-4" *ngIf="record.incidentInfo?.descriptionOfEquipmentMalfunction">
              {{ record.incidentInfo?.descriptionOfEquipmentMalfunction }}
            </p>
          </div>

          <!-- Accident -->
          <div *ngIf="record.incidentInfo?.hasAccident">
            <div class="d-flex align-items-center gap-2 mb-2">
              <i class="bi bi-exclamation-triangle-fill text-danger"></i>
              <span class="fw-medium">Accident</span>
              <span *ngIf="record.incidentInfo?.accidentType" class="badge bg-danger-subtle text-danger">
                {{ record.incidentInfo?.accidentType }}
              </span>
            </div>
            <p class="mb-0 ps-4" *ngIf="record.incidentInfo?.accidentDescription">
              {{ record.incidentInfo?.accidentDescription }}
            </p>
            <p class="mb-0 ps-4 mt-2" *ngIf="record.incidentInfo?.uaLossType">
              <span class="text-muted">UA Loss Type:</span> {{ record.incidentInfo?.uaLossType }}
            </p>
          </div>
        </ng-container>
      </div>
    </div>

    <!-- Section 10: Safety & Compliance -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-shield-check me-2"></i>Safety & Compliance</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">Re-Entry Interval</label>
            <p class="mb-0 fw-medium">{{ record.safetyCompliance?.reentryInterval || 'N/A' }}</p>
          </div>
          <div class="col-md-8">
            <label class="form-label text-muted small mb-1">PPE Required</label>
            <div class="d-flex flex-wrap gap-2" *ngIf="getPpeLabels().length > 0">
              <span *ngFor="let ppe of getPpeLabels()" class="badge rounded-pill bg-info-subtle text-info">
                {{ ppe }}
              </span>
            </div>
            <p class="mb-0 text-muted" *ngIf="getPpeLabels().length === 0">None specified</p>
          </div>
          <div class="col-12" *ngIf="record.safetyCompliance?.rinseEquipmentPostApplication">
            <label class="form-label text-muted small mb-1">Equipment Rinsing Procedures</label>
            <p class="mb-0">{{ record.safetyCompliance?.rinseEquipmentPostApplication }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 11: Emergency Information -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-telephone me-2"></i>Emergency Information</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Emergency Contact Number</label>
            <p class="mb-0">
              <span *ngIf="record.emergencyContact?.emergencyContactNumber">
                <i class="bi bi-telephone me-1"></i>{{ record.emergencyContact?.emergencyContactNumber | phoneFormat }}
              </span>
              <span *ngIf="!record.emergencyContact?.emergencyContactNumber" class="text-muted">N/A</span>
            </p>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">State Emergency Contact</label>
            <p class="mb-0">
              <span *ngIf="record.emergencyContact?.stateEmergencyContactNumber">
                <i class="bi bi-telephone me-1"></i>{{ record.emergencyContact?.stateEmergencyContactNumber | phoneFormat }}
              </span>
              <span *ngIf="!record.emergencyContact?.stateEmergencyContactNumber" class="text-muted">N/A</span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 12: Additional Notes -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-chat-left-text me-2"></i>Additional Notes</h5>
      </div>
      <div class="card-body">
        <p class="mb-0" *ngIf="record.notes">{{ record.notes }}</p>
        <p class="mb-0 text-muted fst-italic" *ngIf="!record.notes">No additional notes</p>
      </div>
    </div>

  </div>

  <!-- Right Column - Summary Sidebar -->
  <div class="col-lg-4">
    <div class="card sticky-top" style="top: 1rem;">
      <div class="card-header p-3">
        <h5 class="mb-0">Record Summary</h5>
      </div>
      <div class="card-body">
        <!-- Field Info -->
        <div class="mb-3">
          <div class="text-muted small">Field</div>
          <div class="fw-medium">{{ field?.name || 'Unknown Field' }}</div>
        </div>
        <div class="mb-3">
          <div class="text-muted small">Total Acreage</div>
          <div>{{ field?.acreage || 'N/A' }} acres</div>
        </div>

        <!-- Drones Summary -->
        <div class="mb-3" *ngIf="record.droneFlights && record.droneFlights.length > 0">
          <div class="text-muted small">Drones ({{ record.droneFlights.length }})</div>
          <div class="mt-1">
            <div *ngFor="let flight of record.droneFlights" class="d-flex justify-content-between small py-1 border-bottom border-light">
              <span class="text-truncate" style="max-width: 140px;" [title]="flight.droneName || 'Unknown'">
                {{ flight.droneName || 'Unknown' }}
              </span>
              <span class="text-muted">
                {{ flight.acreageSprayed }} ac
                <!-- `!= null` so a real 0 shows as "0 flt" rather than falling
                     into the em-dash "not entered" case. -->
                <span *ngIf="flight.numFlights != null"> &middot; {{ flight.numFlights }} flt</span>
                <span *ngIf="flight.numFlights == null" class="fst-italic"> &middot; &mdash;</span>
              </span>
            </div>
          </div>
        </div>

        <!-- Total Acreage Sprayed -->
        <div class="mb-3">
          <div class="text-muted small">Total Sprayed</div>
          <div class="fw-medium text-primary">{{ totalAcreageSprayed }} acres</div>
        </div>

        <!-- Chemicals Summary -->
        <div class="mb-3" *ngIf="record.chemicals && record.chemicals.length > 0">
          <div class="text-muted small">Chemicals ({{ record.chemicals.length }})</div>
          <div class="mt-1">
            <div *ngFor="let chem of record.chemicals" class="d-flex justify-content-between small py-1 border-bottom border-light">
              <span class="text-truncate" style="max-width: 140px;" [title]="chem.name">{{ chem.name }}</span>
              <span class="text-muted">{{ chem.rate || 'N/A' }}</span>
            </div>
          </div>
        </div>

        <!-- Certification -->
        <div class="mb-3">
          <div class="text-muted small">Certification</div>
          <div>{{ certificationName }}</div>
          <div class="small" *ngIf="record.certificationExpiration"
               [class.text-danger]="isCertificationExpired()"
               [class.text-warning]="isCertificationExpiringSoon()">
            <ng-container *ngIf="isCertificationExpired()">
              <i class="bi bi-x-circle me-1"></i>Expired
            </ng-container>
            <ng-container *ngIf="isCertificationExpiringSoon()">
              <i class="bi bi-exclamation-triangle me-1"></i>Expiring soon
            </ng-container>
            <ng-container *ngIf="!isCertificationExpired() && !isCertificationExpiringSoon()">
              Expires: {{ certificationExpiration }}
            </ng-container>
          </div>
          <div class="small text-muted" *ngIf="certificationNumber !== 'N/A'">
            #{{ certificationNumber }}
          </div>
        </div>

        <hr class="my-3">

        <!-- Compliance Checklist Status -->
        <div class="mb-3">
          <div class="text-muted small mb-2">Compliance Status</div>
          <div class="d-flex flex-column gap-2">
            <div class="d-flex align-items-center gap-2">
              <i class="bi" [ngClass]="record.preFlightChecklist ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'"></i>
              <span class="small">Pre-flight checklist</span>
            </div>
            <div class="d-flex align-items-center gap-2">
              <i class="bi" [ngClass]="record.bufferZonesVerified ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'"></i>
              <span class="small">Buffer zones verified</span>
            </div>
          </div>
        </div>

        <hr class="my-3">

        <!-- Action Button -->
        <div class="d-grid">
          <button type="button" class="btn btn-outline-secondary" (click)="goBack()">
            <i class="bi bi-arrow-left me-2"></i>{{ fromOrgId ? 'Back to Organization' : 'Back to List' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Flight Map Modal -->
<div *ngIf="showFlightMapModal" class="modal d-block bg-dark bg-opacity-50" (click)="closeFlightMapModal()">
  <div class="modal-dialog modal-lg modal-dialog-centered modal-fullscreen-md-down">
    <div class="modal-content overflow-hidden" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h5 class="modal-title d-flex align-items-center text-break">
          <i class="bi bi-map me-2"></i>
          <ng-container *ngIf="flightMapModalFlight">{{ flightMapModalFlight.flightPath?.name || 'Flight Path' }}</ng-container>
          <ng-container *ngIf="!flightMapModalFlight">All Flight Paths</ng-container>
        </h5>
        <button type="button" class="btn-close" (click)="closeFlightMapModal()"></button>
      </div>
      <div class="modal-body p-0">
        <app-flight-map
          [flightPath]="flightMapModalFlight?.flightPath ?? null"
          [flightPaths]="flightMapModalFlight ? [] : getAllFlightPaths()"
          [fieldCenter]="getFieldCenter()"
          [showUploadOverlay]="false"
          [height]="'500px'">
        </app-flight-map>
      </div>
      <div class="modal-footer py-2" *ngIf="flightMapModalFlight?.flightPath as path">
        <div class="d-flex justify-content-between align-items-center w-100">
          <div class="small text-muted">
            <i class="bi bi-file-earmark me-1"></i>{{ path.fileName }}
            <span class="mx-2">•</span>
            <i class="bi bi-rulers me-1"></i>{{ path.areaAcres | number:'1.1-1' }} acres
          </div>
          <button type="button" class="btn btn-primary btn-sm" (click)="closeFlightMapModal()">
            Done
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
